'use client';

import { useEffect, useState } from 'react';

// TWA (Trusted Web Activity) context detection for the Android app build.
// Google Play payments policy: no purchase flow may be reachable inside the
// Android app, so every billing surface checks this flag and renders a
// text-only notice instead (no links, no buttons).
//
// Detection: the Android app launches with ?twa=1 (manifest start_url) and
// the first navigation into the app carries an android-app:// referrer.
// The result is persisted in sessionStorage, which is per-tab — normal
// Chrome browsing on the same device is unaffected.

const STORAGE_KEY = 'ics_twa_context';

export function isTwaContext(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.sessionStorage.getItem(STORAGE_KEY) === '1') return true;
    const params = new URLSearchParams(window.location.search);
    const detected =
      params.get('twa') === '1' || document.referrer.startsWith('android-app://');
    if (detected) window.sessionStorage.setItem(STORAGE_KEY, '1');
    return detected;
  } catch {
    return false;
  }
}

// Hook form: returns false on first render (matches server HTML, avoids
// hydration mismatch), then the real value after mount.
export function useTwaContext(): boolean {
  const [twa, setTwa] = useState(false);
  useEffect(() => {
    // Hydration-safe mount detection: server HTML must render the non-TWA
    // view, so the real value can only be read after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTwa(isTwaContext());
  }, []);
  return twa;
}

// Tri-state variant for checkout surfaces (/pricing, /team/new): null until
// detection has run after mount, then the real value. Pages hold a loading
// state while null so payment UI — and the Razorpay <Script> — never mounts
// inside the TWA, not even for one frame.
export function useTwaContextResolved(): boolean | null {
  const [twa, setTwa] = useState<boolean | null>(null);
  useEffect(() => {
    // Same hydration-safe mount pattern as useTwaContext above.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTwa(isTwaContext());
  }, []);
  return twa;
}

// Plain-text message billing surfaces must show inside the TWA.
// Play policy allows telling users where to manage their subscription as
// long as it is not a tappable link or button.
export const TWA_SUBSCRIPTION_NOTICE =
  'To subscribe or manage your plan, visit indiacasestatus.com in your browser.';
